import { cn } from '@/shadcn-ui/utils';
import { TailwindBreakpoints } from '../types/tailwind-breakpoints';

export type SpacerVariant =
  | 'width1'
  | 'width2'
  | 'width4'
  | 'width8'
  | 'width12'
  | 'width16'
  | 'width18'
  | 'width24'
  | 'width32'
  | 'width40'
  | 'height2'
  | 'height4'
  | 'height6'
  | 'height8'
  | 'height12'
  | 'height16'
  | 'height18'
  | 'height20'
  | 'height24'
  | 'height28'
  | 'height32'
  | 'height36'
  | 'height40'
  | 'height48'
  | 'height56'
  | 'height60'
  | 'height64'
  | 'height80'
  | 'height85'
  | 'height88'
  | 'height96'
  | 'height120'
  | 'height144'
  | 'height206'
  | 'height214'
  | 'height222'
  | 'height244'
  | 'height288';

const breakpoints = ['sm', 'md', 'lg', 'xl', '2xl'] as TailwindBreakpoints[];

export interface SpacerProps {
  /** widthINT is for horizontal spacing, heightINT is for vertical spacing */
  variant: SpacerVariant;
  /** Specify all breakpoint sizes you'd like to hide spacer for. Spacer will render for any options not included in this prop */
  hideForBreakpoints?: TailwindBreakpoints[];
}

const breakpointShowHideClasses = {
  'sm': {
    invisible: 'invisible w-0 h-0',
    visible: 'visible w-full h-auto',
  },
  'md': {
    invisible: 'md:invisible md:w-0 md:h-0',
    visible: 'md:visible md:w-full md:h-auto',
  },
  'lg': {
    invisible: 'lg:invisible lg:w-0 lg:h-0',
    visible: 'lg:visible lg:w-full lg:h-auto',
  },
  'xl': {
    invisible: 'xl:invisible xl:w-0 xl:h-0',
    visible: 'xl:visible xl:w-full xl:h-auto',
  },
  '2xl': {
    invisible: '2xl:invisible 2xl:w-0 xl:h-0',
    visible: '2xl:visible 2xl:w-full 2xl:h-auto',
  },
};

export default function Spacer({ variant, hideForBreakpoints }: SpacerProps) {
  const options: Record<typeof variant, string> = {
    width1: 'w-[1px]',
    width2: 'w-[2px]',
    width4: 'w-[4px]',
    width8: 'w-[8px]',
    width12: 'w-[12px]',
    width16: 'w-[16px]',
    width18: 'w-[18px]',
    width24: 'w-[24px]',
    width32: 'w-[32px]',
    width40: 'w-[40px]',
    height2: 'h-[2px]',
    height4: 'h-[4px]',
    height6: 'h-[6px]',
    height8: 'h-[8px]',
    height12: 'h-[12px]',
    height16: 'h-[16px]',
    height18: 'h-[18px]',
    height20: 'h-[20px]',
    height24: 'h-[24px]',
    height28: 'h-[28px]',
    height32: 'h-[32px]',
    height36: 'h-[36px]',
    height40: 'h-[40px]',
    height48: 'h-[48px]',
    height56: 'h-[56px]',
    height60: 'h-[60px]',
    height64: 'h-[64px]',
    height80: 'h-[80px]',
    height85: 'h-[85px]',
    height88: 'h-[88px]',
    height96: 'h-[96px]',
    height120: 'h-[120px]',
    height144: 'h-[144px]',
    height206: 'h-[206px]',
    height214: 'h-[214px]',
    height222: 'h-[222px]',
    height244: 'h-[244px]',
    height288: 'h-[288px]',
  };
  const variantClassName = options[variant];

  if (hideForBreakpoints === undefined) {
    return <div className={cn(variantClassName, 'flex-shrink-0')} />;
  }

  const showHideClassNames = breakpoints
    .map((breakpoint) => {
      if (hideForBreakpoints.includes(breakpoint)) {
        return breakpointShowHideClasses[breakpoint].invisible;
      }

      return breakpointShowHideClasses[breakpoint].visible;
    })
    .join(' ');

  /** If we are hiding for breakpoints, apply the visible/invisible to a container div */
  return (
    <div className={showHideClassNames}>
      <div className={cn(variantClassName, 'flex-shrink-0')} />
    </div>
  );
}
